// Tour editor information architecture: the flat section list grouped into the
// same three job-buckets the venue editor uses (negocio / operación / gestión),
// so the .360 tour rail reads like the .tap venue rail. Single source for the
// sidebar rail AND the Overview hub — they must not diverge.
import {
  BarChart3,
  FileText,
  Image,
  Layers,
  LayoutDashboard,
  Lock,
  Paintbrush,
  Send,
  Settings,
  Share2,
  Shield,
  SlidersHorizontal,
  TriangleAlert,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { conceptIcon } from "@u/conceptIcons";

export type TourSectionKey =
  | "overview"
  | "configuration"
  | "socialMedia"
  | "multimedia"
  | "structure"
  | "property"
  | "autoplay"
  | "features"
  | "branding"
  | "leads"
  | "bookings"
  | "events"
  | "analytics"
  | "avisos";

export type TourGroupKey = "business" | "operation" | "management";

/** Config sub-rail keys (mirror SettingsSection's internal SectionRail). */
export type TourSubKey = string;

export interface TourSub {
  key: TourSubKey;
  labelKey: string;
  icon: LucideIcon;
  /** admin-only sub (e.g. the config › admin panel). */
  adminOnly?: boolean;
}

export interface TourSection {
  key: TourSectionKey;
  labelKey: string;
  icon: LucideIcon;
  group: TourGroupKey;
  /** Internal SectionRail leaves. Single-sub sections use one sub === section. */
  subs: TourSub[];
}

// Reuses the venue editor's group-label keys (Negocio / Operación / Gestión).
export const TOUR_GROUP_LABEL: Record<TourGroupKey, string> = {
  business: "vn_grp_business",
  operation: "vn_grp_operation",
  management: "vn_grp_management",
};

export const TOUR_GROUP_ORDER: TourGroupKey[] = [
  "business",
  "operation",
  "management",
];

/** A single-panel section: its only rail leaf is itself. */
const solo = (
  key: TourSectionKey,
  labelKey: string,
  icon: LucideIcon,
): TourSub[] => [{ key, labelKey, icon }];

export const TOUR_SECTIONS: TourSection[] = [
  {
    key: "overview",
    labelKey: "overview",
    icon: LayoutDashboard,
    group: "business",
    subs: solo("overview", "overview", LayoutDashboard),
  },
  {
    key: "configuration",
    labelKey: "configuration",
    icon: Settings,
    group: "business",
    // Mirrors SettingsSection's internal SectionRail (content/publication/share/
    // access/admin). share+access always show in the editor; admin is admin-only.
    subs: [
      { key: "content", labelKey: "content", icon: FileText },
      { key: "publication", labelKey: "publication", icon: Send },
      { key: "share", labelKey: "share", icon: Share2 },
      { key: "access", labelKey: "access_block", icon: Lock },
      { key: "admin", labelKey: "admin_block", icon: Shield, adminOnly: true },
    ],
  },
  {
    key: "socialMedia",
    labelKey: "social_media",
    icon: Share2,
    group: "business",
    subs: solo("socialMedia", "social_media", Share2),
  },
  {
    key: "multimedia",
    labelKey: "media",
    icon: Image,
    group: "business",
    subs: solo("multimedia", "media", Image),
  },
  {
    key: "structure",
    labelKey: "structure",
    icon: Layers,
    group: "business",
    subs: solo("structure", "structure", Layers),
  },
  {
    key: "property",
    labelKey: "property",
    icon: conceptIcon.property,
    group: "business",
    subs: solo("property", "property", conceptIcon.property),
  },
  {
    key: "autoplay",
    labelKey: "autoplay",
    icon: conceptIcon.autoplay,
    group: "business",
    subs: solo("autoplay", "autoplay", conceptIcon.autoplay),
  },
  {
    key: "features",
    labelKey: "viewer_features",
    icon: SlidersHorizontal,
    group: "business",
    subs: solo("features", "viewer_features", SlidersHorizontal),
  },
  {
    key: "branding",
    labelKey: "branding",
    icon: Paintbrush,
    group: "business",
    subs: solo("branding", "branding", Paintbrush),
  },
  {
    key: "leads",
    labelKey: "leads",
    icon: conceptIcon.leadInbox,
    group: "operation",
    subs: solo("leads", "leads", conceptIcon.leadInbox),
  },
  {
    key: "bookings",
    labelKey: "bookings",
    icon: conceptIcon.booking,
    group: "operation",
    subs: solo("bookings", "bookings", conceptIcon.booking),
  },
  {
    key: "events",
    labelKey: "open_house_title",
    icon: conceptIcon.openHouse,
    group: "operation",
    subs: solo("events", "open_house_title", conceptIcon.openHouse),
  },
  {
    key: "analytics",
    labelKey: "analytics",
    icon: BarChart3,
    group: "management",
    subs: solo("analytics", "analytics", BarChart3),
  },
  {
    key: "avisos",
    labelKey: "health.nav",
    icon: TriangleAlert,
    group: "management",
    subs: solo("avisos", "health.nav", TriangleAlert),
  },
];

/** First rail leaf of a section (the panel a bare deep-link lands on). */
export function defaultTourSub(key: TourSectionKey): TourSubKey {
  return TOUR_SECTIONS.find((s) => s.key === key)?.subs[0]?.key ?? key;
}

/**
 * Single source of "which sections does THIS tour show". Pure — the caller
 * computes the plan/role flags (hooks live in the sidebar). Real-estate no
 * longer carves out leads/bookings/analytics: every tour manages them per-tour.
 */
export function visibleTourSections(flags: {
  isAgency: boolean;
  leadsAllowed: boolean;
}): TourSection[] {
  return TOUR_SECTIONS.filter((s) => {
    if (s.key === "branding") return flags.isAgency;
    if (s.key === "leads") return flags.leadsAllowed;
    return true;
  });
}

/** Sections grouped + ordered for the rail (empty groups dropped). */
export function tourEditorGroups(sections: TourSection[]): {
  group: TourGroupKey;
  items: TourSection[];
}[] {
  return TOUR_GROUP_ORDER.map((group) => ({
    group,
    items: sections.filter((s) => s.group === group),
  })).filter((g) => g.items.length > 0);
}
